import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Paper,
  Typography,
  Stack,
  CircularProgress,
  useTheme,
  alpha,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { MdCelebration } from "react-icons/md";
import { getSecureLocalItem } from "utils/secureSessionStorage";
import { fetchHolidays } from "store/features/company/leaveManagement/holiday/holidayActions";
import {
  selectHolidays,
  selectHolidayLoading,
} from "store/features/company/leaveManagement/holiday/holidaySelectors";

const UpcomingHolidaysWidget = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isDark = theme.palette.mode === "dark";

  const rawHolidays = useSelector(selectHolidays);
  const holidays = useMemo(() => rawHolidays || [], [rawHolidays]);
  const loading = useSelector(selectHolidayLoading);

  useEffect(() => {
    const token = getSecureLocalItem("token");
    if (!token) return;
    dispatch(fetchHolidays()).catch(() => {});
  }, [dispatch]);

  // Process & filter upcoming holidays
  const upcomingHolidays = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return [...holidays]
      .filter((h) => {
        if (!h.holiday_date) return false;
        const hDate = new Date(h.holiday_date);
        hDate.setHours(0, 0, 0, 0);
        return hDate >= today;
      })
      .sort((a, b) => new Date(a.holiday_date) - new Date(b.holiday_date))
      .slice(0, 4);
  }, [holidays]);

  // Fallback: If no future holidays exist in the database, show closest sorted list
  const displayHolidays =
    upcomingHolidays.length > 0
      ? upcomingHolidays
      : [...holidays].slice(0, 4);

  const getDaysRemainingText = (dateString) => {
    if (!dateString) return "";
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const targetDate = new Date(dateString);
    targetDate.setHours(0, 0, 0, 0);

    const diffTime = targetDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Tomorrow";
    if (diffDays > 1) return `In ${diffDays} days`;
    return "";
  };

  const formatDateDetails = (dateString) => {
    if (!dateString) return { month: "-", day: "-", weekday: "-" };
    const date = new Date(dateString);
    return {
      month: date.toLocaleDateString("en-US", { month: "short" }).toUpperCase(),
      day: date.getDate(),
      weekday: date.toLocaleDateString("en-US", { weekday: "long" }),
    };
  };

  const currentYear = new Date().getFullYear();
  const firstHoliday = displayHolidays[0];
  const remainingHolidays = displayHolidays.slice(1);

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 2.5 },
        borderRadius: "8px",
        bgcolor: isDark ? "#111827" : "#ffffff",
        border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0"}`,
        boxShadow: isDark
          ? "0 1px 3px rgba(0,0,0,0.4)"
          : "0 1px 3px rgba(15,23,42,0.03)",
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      {/* Header Bar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
          pb: 1.5,
          borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#f1f5f9"}`,
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: "4px",
              bgcolor: isDark ? alpha("#4f46e5", 0.2) : "#eef2ff",
              color: "#4f46e5",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <MdCelebration size={18} />
          </Box>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              color: "text.primary",
              fontSize: "1rem",
              letterSpacing: "-0.01em",
            }}
          >
            Upcoming Holidays
          </Typography>
        </Stack>

        <Typography
          variant="caption"
          onClick={() => navigate("/company/holiday")}
          sx={{
            fontWeight: 600,
            color: "#4f46e5",
            cursor: "pointer",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {currentYear} Calendar
        </Typography>
      </Box>

      {/* Holidays Content */}
      {loading ? (
        <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
          <CircularProgress size={24} sx={{ color: "primary.main" }} />
        </Box>
      ) : displayHolidays.length === 0 ? (
        <Box sx={{ py: 3, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            No upcoming holidays scheduled.
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1.5}>
          {/* Featured First Holiday */}
          {firstHoliday && (() => {
            const { month, day, weekday } = formatDateDetails(firstHoliday.holiday_date);
            const daysRemaining = getDaysRemainingText(firstHoliday.holiday_date);

            return (
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: "6px",
                  bgcolor: isDark ? alpha("#4f46e5", 0.08) : "#f0f4ff",
                  border: `1px solid ${isDark ? alpha("#4f46e5", 0.25) : "#e0e7ff"}`,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                {/* Date Square Badge */}
                <Box
                  sx={{
                    width: 44,
                    height: 48,
                    borderRadius: "4px",
                    bgcolor: isDark ? "#1e293b" : "#ffffff",
                    border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "#dbeafe"}`,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "0.62rem",
                      fontWeight: 800,
                      color: "#4f46e5",
                      lineHeight: 1,
                      letterSpacing: "0.05em",
                    }}
                  >
                    {month}
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 800,
                      color: "text.primary",
                      lineHeight: 1.1,
                      mt: 0.2,
                    }}
                  >
                    {day}
                  </Typography>
                </Box>

                {/* Holiday Info */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 700,
                        color: "text.primary",
                        fontSize: "0.9rem",
                        lineHeight: 1.2,
                      }}
                    >
                      {firstHoliday.holiday_name || firstHoliday.name || "Company Holiday"}
                    </Typography>
                    {daysRemaining && (
                      <Box
                        component="span"
                        sx={{
                          fontSize: "0.68rem",
                          fontWeight: 700,
                          px: 1,
                          py: 0.2,
                          borderRadius: "4px",
                          bgcolor: isDark ? alpha("#6366f1", 0.25) : "#4f46e5",
                          color: "#ffffff",
                        }}
                      >
                        {daysRemaining}
                      </Box>
                    )}
                  </Stack>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      fontWeight: 500,
                      display: "block",
                      fontSize: "0.75rem",
                    }}
                  >
                    {weekday} • {firstHoliday.holiday_type || "Federal & Company Holiday"}
                  </Typography>
                </Box>
              </Box>
            );
          })()}

          {/* Secondary Holidays */}
          {remainingHolidays.map((holiday, idx) => {
            const { month, day, weekday } = formatDateDetails(holiday.holiday_date);
            const daysRemaining = getDaysRemainingText(holiday.holiday_date);

            return (
              <Box
                key={holiday.id || idx}
                sx={{
                  p: 1.25,
                  borderRadius: "6px",
                  bgcolor: isDark ? "rgba(255,255,255,0.02)" : "#ffffff",
                  border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#f1f5f9"}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1.5,
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                  {/* Compact Date Box */}
                  <Box
                    sx={{
                      width: 40,
                      height: 42,
                      borderRadius: "4px",
                      bgcolor: isDark ? "rgba(255,255,255,0.04)" : "#f8fafc",
                      border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0"}`,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "0.58rem",
                        fontWeight: 700,
                        color: "text.secondary",
                        lineHeight: 1,
                      }}
                    >
                      {month}
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 800,
                        color: "text.primary",
                        lineHeight: 1.1,
                      }}
                    >
                      {day}
                    </Typography>
                  </Box>

                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 700,
                        color: "text.primary",
                        fontSize: "0.84rem",
                        lineHeight: 1.2,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {holiday.holiday_name || holiday.name || "Holiday"}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        fontSize: "0.72rem",
                        display: "block",
                      }}
                    >
                      {weekday} • {holiday.holiday_type || "Office Closed"}
                    </Typography>
                  </Box>
                </Stack>

                {daysRemaining && (
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: "text.secondary",
                      fontSize: "0.74rem",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {daysRemaining}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Stack>
      )}
    </Paper>
  );
};

export default UpcomingHolidaysWidget;
